import { useCallback, useEffect, useState } from 'react';

// Measures the space between the bottom of the returned ref's element and the
// bottom of the window. Unlike a hardcoded `calc(100vh - Npx)`, this stays
// correct at any browser zoom level, since zoom changes how tall the header
// actually renders (font metrics, wrapping, etc.) without changing the fixed
// px guess.
//
// The ref is a callback ref (state-backed) rather than a plain useRef object.
// This component's page swaps between an overlay/loader and the real content
// (while fetching, and again on any later refetch), which unmounts and
// remounts the measured header node. A plain useRef + effect-with-stable-deps
// only ever measures the first node it saw and goes stale once that node is
// replaced - getBoundingClientRect() on a detached node returns all zeroes,
// which silently breaks the measurement. Storing the node in state makes the
// effect re-attach every time the node actually changes.
export function useAvailableHeight({ bottomGap = 24, min = 200, debugLabel, debugOldGuess } = {}) {
    const [height, setHeight] = useState(min);
    const [node, setNode] = useState(null);
    const ref = useCallback((el) => setNode(el), []);

    useEffect(() => {
        if (!node) return;

        let rafId = null;

        const measure = () => {
            const { bottom } = node.getBoundingClientRect();
            const measured = Math.max(min, Math.round(window.innerHeight - bottom - bottomGap));
            setHeight((prev) => (Math.abs(prev - measured) <= 1 ? prev : measured));

            if (debugLabel) {
                const oldGuess = debugOldGuess != null ? window.innerHeight - debugOldGuess : null;
                // TEMP: compares the live measurement against the old hardcoded `100vh - Npx` guess.
                // eslint-disable-next-line no-console
                console.log(
                    `[useAvailableHeight:${debugLabel}] zoom~${window.devicePixelRatio.toFixed(2)} ` +
                    `innerHeight=${window.innerHeight} headerBottom=${Math.round(bottom)} ` +
                    `measured=${measured} oldGuess=${oldGuess} delta=${oldGuess != null ? measured - oldGuess : 'n/a'}`,
                );
            }
        };

        // Resize/zoom fire a burst of notifications while the layout is still
        // settling (e.g. text re-wrapping at the new zoom level). Coalescing
        // them onto a single rAF means only the final, settled measurement is
        // applied instead of every intermediate one - otherwise each step in
        // the burst nudges the table's scroll height, which renders as the
        // table visibly growing in stages instead of resizing once.
        const recompute = () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = null;
                measure();
            });
        };

        measure();

        const ro = new ResizeObserver(recompute);
        ro.observe(node);
        window.addEventListener('resize', recompute);

        return () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            ro.disconnect();
            window.removeEventListener('resize', recompute);
        };
    }, [node, bottomGap, min, debugLabel, debugOldGuess]);

    return [ref, height];
}
